import { getMsalIdToken, handleTokenExpiration } from './MsalTokenService';
import logger from 'Services/MyTuiProfileCustomLogger';

/**
 * Setup request interceptor to add Authorization header
 * @param {object} axiosInstance - Axios instance to configure
 */
export const setupRequestInterceptor = (axiosInstance) => {
  axiosInstance.interceptors.request.use(
    async (config) => {
      try {
        // Acquire ID token
        const idToken = await getMsalIdToken();

        // Add Authorization header with Bearer token
        if (idToken) {
          config.headers.Authorization = `Bearer ${idToken}`;
        }

        return config;
      } catch (error) {
        // Auth errors (expired session, no account) are expected flow — log as warning, not error.
        // They will be handled upstream by RestClientService or initializeApp.
        const isAuthError =
          error.message?.includes('No account found') || error.message?.includes('Redirecting to login');

        if (!isAuthError) {
          logger.error('Error in request interceptor:', error);
        } else {
          logger.warn('Auth session not available in request interceptor', { message: error.message });
        }

        // Propagate error to prevent request from being sent
        return Promise.reject(error);
      }
    },
    (error) => {
      // Handle request setup errors
      return Promise.reject(error);
    }
  );
};

/**
 * Setup response interceptor to handle 401 responses
 * @param {object} axiosInstance - Axios instance to configure
 */
export const setupResponseInterceptor = (axiosInstance) => {
  axiosInstance.interceptors.response.use(
    (response) => {
      // Pass through successful responses
      return response;
    },
    async (error) => {
      const originalRequest = error.config;

      // Check if error is 401 and we haven't already retried
      if (error.response?.status === 401 && !originalRequest._retry) {
        originalRequest._retry = true;

        try {
          // Attempt to renew the token
          const newToken = await handleTokenExpiration();

          // Update the Authorization header with new token
          originalRequest.headers.Authorization = `Bearer ${newToken}`;

          // Retry the original request with new token
          return axiosInstance.request(originalRequest);
        } catch (renewalError) {
          // Token renewal failed, reject with original error
          logger.error('Token renewal failed:', renewalError);
          return Promise.reject(error);
        }
      }

      // For non-401 errors or already retried requests, reject with error
      return Promise.reject(error);
    }
  );
};
